import React from 'react';
import { useTranslation } from 'react-i18next';
import { SpinnerOverlay } from '../ui';
import HeroSection from "../sections/hero/HeroSection";
import PrayerRequestSection from "../sections/prayerRequest/PrayerRequestSection";
import { homeService } from '../../services/home-service';
import { Language } from '../../constants/language';
import EventsSection from "../sections/events/EventsSection";
import {Ministry} from "../../models/ministry/ministry";
import {School} from "../../models/school/school";
import {Event} from "../../models/event/event";
import MinistriesSection from "../sections/ministries/MinistriesSection";
import SchoolsSection from "../sections/schools/SchoolsSection";
import { ProductsSection } from "../sections/store/products";
import { usePageSEO } from '../../hooks';

const Home: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the home page
  usePageSEO('home');

  const { t, i18n } = useTranslation();
  const [loadingSpinner, setLoadingSpinner] = React.useState(false);
  const isRTL = i18n.language === 'ar';

  const [upcomingEvents, setUpcomingEvents] = React.useState<Event[]>([]);
  const [featuredMinistries, setFeaturedMinistries] = React.useState<Ministry[]>([]);
  const [featuredSchools, setFeaturedSchools] = React.useState<School[]>([]);
  const [featuredProducts, setFeaturedProducts] = React.useState<any[]>([]);


  React.useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoadingSpinner(true);
      try {
        const lang = (i18n.language === 'ar' ? Language.AR : Language.EN);
        const {upcomingEvents, featuredMinistries, featuredSchools, featuredProducts } = await homeService.loadHomePageData(lang);

        setUpcomingEvents(upcomingEvents);
        setFeaturedMinistries(featuredMinistries);
        setFeaturedSchools(featuredSchools);
        setFeaturedProducts(featuredProducts);

      } catch (err) {
      } finally {
        if (!isCancelled) setLoadingSpinner(false);
      }
    };

    fetchData().then();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [i18n.language]);

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>

      {loadingSpinner && (
        <SpinnerOverlay show={true} message={t('ui.loading')} size="md" color="white" backdropOpacity={0.8} />
      )}

      <section className="relative">
        <HeroSection />
      </section>

      {/* Upcoming Events Section - White Background */}
      <section className="bg-white">
        <EventsSection  events= {upcomingEvents}
                        title= {t('pages.events.upcomingEvents')}
                        description={t('pages.events.heroSubtitle')}
                        loading={loadingSpinner}
                        limit= {3}
        />
      </section>

      {/* Featured Ministries Section - Black Background */}
      <section className="bg-black">
        <MinistriesSection ministries={featuredMinistries}
                           title={t('pages.home.ministries.title')}
                           description={t('pages.home.ministries.subtitle')}
                           loading={loadingSpinner}
                           limit={3}
       />
      </section>

      {/* Schools Section - White Background */}
      <section className="bg-white">
        <SchoolsSection schools={featuredSchools}
                        title={t('pages.home.schools.title')}
                        description={t('pages.home.schools.subtitle')}
                        showEnrollmentProgress={false}
                        loading={loadingSpinner}
        />
      </section>

      <section className="bg-black">
        <ProductsSection products={featuredProducts}
                          title={t('pages.home.storeSection.title')}
                          description={t('pages.home.storeSection.subtitle')}
                          loading={loadingSpinner}
                          limit={3}
                         />
      </section>

      {/* Spiritual Literature Collection - Black Background */}
      {/*<section className="bg-black text-white py-20">*/}
      {/*  <div className="max-w-7xl mx-auto px-6">*/}
      {/*    <div className="text-center mb-16">*/}
      {/*      <div className="inline-flex items-center justify-center w-16 h-16 bg-white text-black rounded-full mb-6">*/}
      {/*        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />*/}
      {/*        </svg>*/}
      {/*      </div>*/}
      {/*      */}
      {/*      <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">*/}
      {/*        {t('pages.home.storeSection.title')}*/}
      {/*      </h2>*/}
      {/*      */}
      {/*      <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">*/}
      {/*        {t('pages.home.storeSection.subtitle')}*/}
      {/*      </p>*/}
      {/*    </div>*/}

      {/*    <ProductsGrid */}
      {/*      productType={ProductType.BOOK}*/}
      {/*      limit={3}*/}
      {/*      featured={true}*/}
      {/*      showLoadingState={true}*/}
      {/*      showEmptyState={true}*/}
      {/*    />*/}

      {/*    <div className="text-center mt-12">*/}
      {/*      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 max-w-2xl mx-auto border border-gray-600 shadow-lg">*/}
      {/*        <h3 className="text-2xl font-bold text-white mb-4">*/}
      {/*          {t('pages.home.storeSection.buildLibrary.title')}*/}
      {/*        </h3>*/}
      {/*        <p className="text-gray-300 mb-6 leading-relaxed">*/}
      {/*          {t('pages.home.storeSection.buildLibrary.description')}*/}
      {/*        </p>*/}
      {/*        <Link to={ROUTES.STORE}>*/}
      {/*          <Button */}
      {/*            size="lg"*/}
      {/*            className="bg-white text-black hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"*/}
      {/*          >*/}
      {/*            {t('pages.home.storeSection.buildLibrary.button')}*/}
      {/*            <svg className={`w-5 h-5 ${i18n.language === 'ar' ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i18n.language === 'ar' ? "M15 19l-7-7 7-7" : "M13 7l5 5m0 0l-5 5m5-5H6"} />*/}
      {/*            </svg>*/}
      {/*          </Button>*/}
      {/*        </Link>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}

      {/* Prayer Request Section - White Background */}
      <section id="prayer-request" className="bg-white text-black py-20">
        <PrayerRequestSection />
      </section>


    </div>
  );
};

export default Home;
